import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { getUserData, getUserHubspotSubscriptions } from '@services/user';
import { HubspotApiResponse, HubspotData, Municipality, UserSettingsContextValue } from './components/Settings.types';


const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
  const [userData, setUserData] = useState<{ [key: string]: unknown }>({});
  const [hubspotData, setHubspotData] = useState<HubspotData>(null);

  async function fetchUserData() {
    const resp = await getUserData();
    if (resp.ok && resp.data && typeof resp.data === 'object') {
      setUserData(resp.data as { [key: string]: unknown });
    }
  }

  async function fetchHubspotData() {
    const resp = await getUserHubspotSubscriptions();
    if (resp.ok && resp.data && typeof resp.data === 'object') {
      const data = resp.data as HubspotApiResponse;
      setHubspotData({
        hubspot_user_subscriptions: data.hubspot_subscriptions ?? data.hubspot_user_subscriptions,
        hubspot_subscription_definitions: data.hubspot_subscription_definitions ?? [],
      });
    }
  }

  useEffect(() => {
    fetchUserData();
    fetchHubspotData();
  }, []);

  const { requiredFields, munis } = useMemo(() => ({
    requiredFields: userData?.requiredFields ?? null,
    munis: userData?.munis as Municipality[] | undefined,
  }), [userData?.requiredFields, userData?.munis]);

  const refetchUserData = useCallback(() => { fetchUserData(); }, []);

  const refetchHubspotData = useCallback(() => { fetchHubspotData(); }, []);

  const value = useMemo<UserSettingsContextValue>(() => ({
    userData,
    hubspotData,
    requiredFields,
    munis,
    refetchUserData,
    refetchHubspotData,
  }), [userData, hubspotData, requiredFields, munis, refetchUserData, refetchHubspotData]);

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const ctx = useContext(UserSettingsContext);
  if (!ctx) {
    throw new Error('useUserSettings must be used within UserSettingsProvider');
  }
  return ctx;
}
